// src/App.js
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SocioCard from "./components/SocioCard";
import AltaSocio from "./components/AltaSocio";
import DocumentList from "./components/DocumentList";
import DocumentForm from "./components/DocumentForm";
import { listarSocios, batchAlertas } from "./services/sociosApi";
import { getToken, isEmpleado, getSocioId } from "./services/auth";

function normRow(row) {
  if (!row) return row;
  const fecha_baja = row.fecha_baja ?? row.fechaBaja ?? row.baja_fecha ?? null;
  const estado =
    row.estado ??
    row.status ??
    (row.activo === false || row.en_baja === true || row.enBaja === true ? "BAJA" : row.estado);
  return { ...row, fecha_baja, estado };
}

export default function App() {
  const navigate = useNavigate();
  const puedeAdministrar = isEmpleado();
  const aliveRef = useRef(true);

  const [reload, setReload] = useState(false);
  const [socios, setSocios] = useState([]);
  const [alertas, setAlertas] = useState({});
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  // UI
  const [buscar, setBuscar] = useState("");
  const [mostrarAlta, setMostrarAlta] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("TODOS"); // TODOS | ACTIVO | TRAMITE | BAJA

  // mapas que actualizan las tarjetas
  const [tramiteById, setTramiteById] = useState({}); // id -> true/false
  const [bajaById, setBajaById] = useState({});       // id -> fecha|null

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const cargar = useCallback(async () => {
  if (!getToken() || !puedeAdministrar) return;
  setCargando(true); setError("");
  try {
    const [rows, alerts] = await Promise.all([ listarSocios(), batchAlertas().catch(() => []) ]);
    if (!aliveRef.current) return;

    const norm = Array.isArray(rows) ? rows.map(normRow) : [];

    // 🔠 Orden alfabético por nombre (secundario: id ascendente)
    norm.sort((a, b) => {
      const nameA = (a?.nombre || "").trim();
      const nameB = (b?.nombre || "").trim();
      const byName = nameA.localeCompare(nameB, "es", { sensitivity: "base" });
      if (byName !== 0) return byName;
      return Number(a?.id ?? 0) - Number(b?.id ?? 0);
    });

    setSocios(norm);

    // (tu lógica de alertas si sigue debajo)
    const map = {};
    if (Array.isArray(alerts)) {
      for (const a of alerts) {
        if (!a || a.id == null) continue;
        const key = String(a.id);
        let val = String(a.alert ?? "none").toLowerCase();
        if (val === "alto") val = "high";
        if (val === "media" || val === "medio") val = "med";
        if (!["high", "med", "none"].includes(val)) val = "none";
        map[key] = val;
      }
    }
    setAlertas(map);
  } catch (e) {
    console.error("Error cargando socios:", e);
    setSocios([]); setAlertas({}); setError("No se pudieron cargar los socios.");
  } finally {
    setCargando(false);
  }
}, [puedeAdministrar]);

  useEffect(() => {
    if (!getToken()) return;
    if (!puedeAdministrar) {
      const myId = getSocioId();
      if (myId != null) navigate(`/socios/${myId}`, { replace: true });
      return;
    }
    cargar();
  }, [navigate, puedeAdministrar, cargar]);

  const handleReload = () => setReload((x) => !x);

  /* === Estado lógico (usa overrides de tarjetas si existen) === */
  function getEstadoLogico(row) {
    if (bajaById[row.id]) return "BAJA";               // tarjeta dijo BAJA
    if (tramiteById[row.id]) return "TRAMITE";         // tarjeta dijo TRÁMITE
    const isBaja =
      !!(row?.fecha_baja && String(row.fecha_baja).trim()) ||
      String(row?.estado || "").toUpperCase().includes("BAJA");
    if (isBaja) return "BAJA";
    const enTramiteFlag = !!row?.enTramite || Number(row?.pendientes) > 0;
    if (enTramiteFlag) return "TRAMITE";
    return "ACTIVO";
  }

  const counts = useMemo(() => {
    const acc = { activo: 0, tramite: 0, baja: 0 };
    for (const s of socios) {
      const est = getEstadoLogico(s);
      if (est === "BAJA") acc.baja++;
      else if (est === "TRAMITE") acc.tramite++;
      else acc.activo++;
    }
    return acc;
  }, [socios, tramiteById, bajaById]);

  const sociosBuscados = useMemo(() => {
    const q = (buscar || "").toLowerCase().trim();
    if (!q) return socios;
    return socios.filter((s) => `${s?.nombre || ""} ${s?.matricula || ""}`.toLowerCase().includes(q));
  }, [socios, buscar]);

  const sociosFiltrados = useMemo(() => {
    if (filtroEstado === "TODOS") return sociosBuscados;
    return sociosBuscados.filter((s) => getEstadoLogico(s) === filtroEstado);
  }, [sociosBuscados, filtroEstado, tramiteById, bajaById]);

  /* === Callbacks desde las tarjetas === */
  const handleChecklistSummary = useCallback((id, pendientes) => {
    setTramiteById((prev) => {
      const next = !!(Number(pendientes) > 0);
      if (prev[id] === next) return prev;
      return { ...prev, [id]: next };
    });
  }, []);

  const handleHydratedStatus = useCallback(({ id, fecha_baja, estado }) => {
    setBajaById((prev) => {
      const v = fecha_baja && String(fecha_baja).trim() ? fecha_baja : null;
      if (prev[id] === v) return prev;
      return { ...prev, [id]: v };
    });
  }, []);

  // Si no es empleado, no renderizamos (router redirige)
  if (!puedeAdministrar) return null;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
        {/* Nav superior izquierda */}
      <div className="max-w-6xl mx-auto mb-2">
        <button
          onClick={() => navigate("/facturacion")}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-gray-50"
          title="Ir a Facturación"
        >
          <span>💶</span>
          <span className="font-medium">Facturación</span>
        </button>
      </div>
      <h1 className="text-3xl font-bold text-center mb-6">📚 Gestor de Documentos - Asotrans</h1>

      {/* Barra superior: búsqueda + acciones */}
      <div className="mb-4 max-w-4xl mx-auto flex gap-2 items-center">
        <input
          type="text"
          placeholder="Buscar socio por nombre o matrícula..."
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          className="flex-1 p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button onClick={() => setMostrarAlta(true)} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
          ➕ Nuevo socio
        </button>
        <button onClick={() => navigate("/alta/wizard")} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          🧭 Wizard alta
        </button>
      </div>

      {/* Filtros por estado */}
      <div className="max-w-4xl mx-auto flex flex-wrap gap-2 mb-6">
        {[
          { key: "TODOS",   label: `Todos (${sociosBuscados.length})` },
          { key: "ACTIVO",  label: `Activos (${counts.activo})` },
          { key: "TRAMITE", label: `En trámite (${counts.tramite})` },
          { key: "BAJA",    label: `Baja (${counts.baja})` },
        ].map((btn) => (
          <button
            key={btn.key}
            onClick={() => setFiltroEstado(btn.key)}
            className={`px-3 py-1 rounded-full border text-sm ${
              filtroEstado === btn.key ? "bg-blue-600 text-white border-blue-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {error && <div className="max-w-4xl mx-auto mb-4 text-red-600">{error}</div>}

      {/* Grid de socios */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-12">
        {cargando ? (
          <p className="col-span-full text-center text-gray-500">Cargando socios…</p>
        ) : sociosFiltrados.length > 0 ? (
          sociosFiltrados.map((socio) => (
            <SocioCard
              key={socio.id}
              id={socio.id}
              nombre={socio.nombre}
              matricula={socio.matricula}
              imagen={socio.imagen || undefined}
              estado={socio.estado}
              fecha_baja={socio.fecha_baja}
              alertLevel={alertas[String(socio.id)] || "none"}
              prefetch="visible"
              onChecklistSummaryChange={handleChecklistSummary} // ← NUEVO
              onHydratedStatus={handleHydratedStatus}           // ← NUEVO
              onChanged={cargar}
            />
          ))
        ) : (
          <p className="col-span-full text-center text-gray-500">
            {buscar || filtroEstado !== "TODOS" ? "No hay socios en este filtro." : "No hay socios disponibles."}
          </p>
        )}
      </div>

      {/* Documentos globales */}
      <div className="max-w-4xl mx-auto">
        <DocumentForm onSuccess={handleReload} />
        <DocumentList reload={reload} />
      </div>

      {/* Modal de AltaSocio */}
      {mostrarAlta && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center p-4">
          <div className="bg-white max-h-[95vh] overflow-y-auto p-6 rounded shadow-xl w-full max-w-3xl">
            <AltaSocio
              onCancel={() => setMostrarAlta(false)}
              onComplete={() => { setMostrarAlta(false); setReload((x) => !x); cargar(); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
